import { DealActivity } from './api/deal';

/**
 * Счета и оплаты за перевозку по сделке.
 *
 * Экспедитор выставляет счёт в той валюте, в которой выставлен его счёт в бухгалтерии (₸, $, € и т. д.),
 * курс здесь не хранится. Клиент платит в валюте счёта. Итоги и задолженность считаются по каждой валюте отдельно.
 *
 * Отдельного хранилища на сервере пока нет, поэтому каждый счёт и каждая оплата — событие сделки
 * со строго размеченным текстом (и, при желании, файлом):
 *   «Счета на оплату»       DOC_INVOICES_PAYMENT: «Счёт: № 12 · 18.10.2026 · 452120 KZT»  (или «· 1000 USD»)
 *                           (старый формат с курсом «… · 1000 USD × 452.12 = 452120 KZT» читается как счёт в ₸)
 *     изменение             «Счёт № 12 изменён: № 12 · 19.10.2026 · 450000 KZT»   — только в течение 10 минут
 *     отзыв                 «Счёт № 12 отозван · причина»                          — в любое время
 *   «Платежные поручения»   DOC_PAYMENT_ORDERS:   «Оплата: 452120 KZT · 20.10.2026 · комментарий»
 *     изменение             «Оплата #389 изменена: 450000 KZT · 20.10.2026 · комментарий» — автор, 10 минут
 *     отзыв                 «Оплата #389 (452120 KZT) отозвана · причина»
 *     запрос на отзыв       «Запрос на отзыв оплаты #389 (452120 KZT) · причина» — событие-запрос экспедитору;
 *                           экспедитор отвечает отметкой «выполнено» с текстом «Согласовано» или «Отклонено»
 *
 * Правила (проверяются и здесь, при разборе событий, — не только кнопками):
 *   - счёт меняет и отзывает только экспедитор; изменение — в течение 10 минут после выставления;
 *   - платёжку клиента экспедитор подтверждает (событие-запрос → «выполнено»);
 *   - отзывает оплату только клиент: свою неподтверждённую платёжку — сразу; подтверждённую оплату
 *     (и внесённую экспедитором) — только с согласия экспедитора;
 *   - оплату меняет только тот, кто её внёс, в течение 10 минут (платёжку клиента — пока она не подтверждена);
 * Отозванные счета и оплаты остаются в списке зачёркнутыми и в итоги не входят.
 * Когда бэкенд добавит поля счетов и оплат — заменить на них (см. ТЗ).
 */
export const INVOICE_ACTIVITY_CODE = 'DOC_INVOICES_PAYMENT';
export const PAYMENT_ACTIVITY_CODE = 'DOC_PAYMENT_ORDERS';

/** Валюты счёта. */
export const INVOICE_CURRENCIES = ['KZT', 'USD', 'EUR', 'CNY', 'RUB'];

/** Сколько можно менять счёт или оплату после отправки. */
export const EDIT_WINDOW_MS = 10 * 60 * 1000;
/** Запас на расхождение часов клиента и сервера при разборе событий. */
const EDIT_GRACE_MS = 60 * 1000;

/** Ответ экспедитора на запрос отзыва оплаты. */
export const REVOKE_APPROVED = 'Согласовано';
export const REVOKE_DECLINED = 'Отклонено';

const OWNER = 'ROLE_CARGO_OWNER';
const FORWARDER = 'ROLE_FREIGHT_FORWARDER';

export interface DealInvoice {
  activityId: number;
  number: string;
  /** Дата счёта (дд.мм.гггг). */
  date: string;
  amount: number;
  currency: string;
  createdAt: string;
  /** Счёт изменён экспедитором после выставления. */
  edited: boolean;
  /** Счёт отозван — в итоги не входит. */
  revoked: boolean;
  revokeReason: string;
  /** Все номера счёта (до и после изменений) — по ним ищется файл. */
  numbers: string[];
}

export interface DealPayment {
  activityId: number;
  amount: number;
  currency: string;
  /** Дата оплаты (дд.мм.гггг). */
  date: string;
  note: string;
  createdAt: string;
  /** Платёжку отправил грузовладелец. */
  fromOwner: boolean;
  /** Платёжка клиента ещё не подтверждена экспедитором — в «Оплачено» не входит. */
  pending: boolean;
  edited: boolean;
  /** Оплата отозвана — в итоги не входит. */
  revoked: boolean;
  revokeReason: string;
  /** Запрос клиента на отзыв подтверждённой платёжки. */
  revokeRequest: { activityId: number; reason: string; state: 'pending' | 'declined' } | null;
  /** События с файлами платёжки: исходное и изменения — свежие в конце. */
  fileActivityIds: number[];
}

/** Итоги по одной валюте. */
export interface CurrencyTotals {
  currency: string;
  invoiced: number;
  paid: number;
  /** Отправлено клиентом и ждёт подтверждения экспедитора. */
  pending: number;
  debt: number;
  /** Подтверждённых оплат больше, чем выставлено, — зачтётся в следующий счёт в этой валюте. */
  overpaid: number;
}

const num = (s: string) => parseFloat(s.replace(/\s/g, '').replace(',', '.'));
const time = (s: string | undefined) => (s ? new Date(s).getTime() : NaN);
const byCreated = <T extends { createdAt: string }>(a: T, b: T) => time(a.createdAt) - time(b.createdAt);
const round2 = (v: number) => Math.round(v * 100) / 100;
const clean = (s: string) => s.trim().replace(/·/g, '-');
/** Событие пришло в течение 10 минут после исходного. */
const inWindow = (origin: string, at: string) => time(at) - time(origin) <= EDIT_WINDOW_MS + EDIT_GRACE_MS;

/** Можно ли ещё менять: прошло меньше 10 минут с отправки. */
export function isEditable(createdAt: string, now = Date.now()): boolean {
  return now - time(createdAt) < EDIT_WINDOW_MS;
}

/** Текст события для нового счёта. */
export function formatInvoiceMessage(number: string, date: string, amount: number, currency: string): string {
  return `Счёт: № ${clean(number)} · ${date} · ${round2(amount)} ${currency}`;
}

/** Текст события «счёт изменён». */
export function formatInvoiceEditMessage(oldNumber: string, number: string, date: string, amount: number, currency: string): string {
  return `Счёт № ${clean(oldNumber)} изменён: № ${clean(number)} · ${date} · ${round2(amount)} ${currency}`;
}

/** Текст события «счёт отозван». */
export function formatInvoiceRevokeMessage(number: string, reason?: string): string {
  const head = `Счёт № ${clean(number)} отозван`;
  return reason?.trim() ? `${head} · ${reason.trim()}` : head;
}

/** Текст события для новой оплаты. */
export function formatPaymentMessage(amount: number, currency: string, date: string, note?: string): string {
  const parts = [`Оплата: ${round2(amount)} ${currency}`, date];
  if (note?.trim()) parts.push(note.trim().replace(/\s*·\s*/g, ', '));
  return parts.join(' · ');
}

/** Текст события «оплата изменена». */
export function formatPaymentEditMessage(p: DealPayment, amount: number, currency: string, date: string, note?: string): string {
  return formatPaymentMessage(amount, currency, date, note).replace(/^Оплата:/, `Оплата #${p.activityId} изменена:`);
}

/** Текст события «оплата отозвана». */
export function formatPaymentRevokeMessage(p: DealPayment, reason?: string): string {
  const head = `Оплата #${p.activityId} (${round2(p.amount)} ${p.currency}) отозвана`;
  return reason?.trim() ? `${head} · ${reason.trim()}` : head;
}

/** Текст запроса клиента на отзыв подтверждённой оплаты. */
export function formatPaymentRevokeRequestMessage(p: DealPayment, reason?: string): string {
  const head = `Запрос на отзыв оплаты #${p.activityId} (${round2(p.amount)} ${p.currency})`;
  return reason?.trim() ? `${head} · ${reason.trim()}` : head;
}

/** Счета из событий сделки — по порядку выставления, с учётом изменений и отзывов. */
export function parseInvoices(activities: DealActivity[] | undefined | null): DealInvoice[] {
  const legacy = /^Счёт:\s*№\s*([^·]+?)\s*·\s*(\d{2}\.\d{2}\.\d{4})\s*·\s*[\d\s.,]+\s+[A-Z]{3}\s*×\s*[\d\s.,]+\s*=\s*([\d\s.,]+?)\s*KZT/;
  const plain = /^Счёт:\s*№\s*([^·]+?)\s*·\s*(\d{2}\.\d{2}\.\d{4})\s*·\s*([\d\s.,]+?)\s*([A-Z]{3})\b/;
  const edit = /^Счёт\s*№\s*(.+?)\s+изменён:\s*№\s*([^·]+?)\s*·\s*(\d{2}\.\d{2}\.\d{4})\s*·\s*([\d\s.,]+?)\s*([A-Z]{3})\b/;
  const revoke = /^Счёт\s*№\s*(.+?)\s+отозван(?:\s*·\s*([\s\S]*))?$/;
  const list: DealInvoice[] = [];
  // Последний действующий счёт с этим номером
  const find = (n: string) => [...list].reverse().find(i => !i.revoked && i.number === n.trim());
  (activities ?? [])
    .filter(a => a.activityType?.code === INVOICE_ACTIVITY_CODE)
    .sort(byCreated)
    .forEach(a => {
      const text = (a.actorMessage || '').trim();
      const byForwarder = a.actorRole?.code === FORWARDER;
      let m: RegExpMatchArray | null;
      if ((m = text.match(edit))) {
        const inv = find(m[1]);
        const amount = num(m[4]);
        if (!inv || !byForwarder || !inWindow(inv.createdAt, a.createdAt) || !isFinite(amount) || amount <= 0) return;
        Object.assign(inv, { number: m[2].trim(), date: m[3], amount, currency: m[5], edited: true });
        if (!inv.numbers.includes(inv.number)) inv.numbers.push(inv.number);
        return;
      }
      if ((m = text.match(revoke))) {
        const inv = find(m[1]);
        if (inv && byForwarder) Object.assign(inv, { revoked: true, revokeReason: (m[2] || '').trim() });
        return;
      }
      const old = text.match(legacy);
      m = old ?? text.match(plain);
      if (!m) return;
      const amount = num(m[3]);
      if (!isFinite(amount) || amount <= 0) return;
      list.push({
        activityId: a.id, number: m[1].trim(), date: m[2], amount, currency: old ? 'KZT' : m[4], createdAt: a.createdAt,
        edited: false, revoked: false, revokeReason: '', numbers: [m[1].trim()],
      });
    });
  return list;
}

/** Оплаты из событий сделки — по порядку внесения, с учётом изменений, отзывов и запросов на отзыв. */
export function parsePayments(activities: DealActivity[] | undefined | null): DealPayment[] {
  const re = /^Оплата:\s*([\d\s.,]+)\s+([A-Z]{3})(?:\s*·\s*(\d{2}\.\d{2}\.\d{4}))?(?:\s*·\s*([\s\S]*))?$/;
  const edit = /^Оплата\s*#(\d+)\s+изменена:\s*([\d\s.,]+)\s+([A-Z]{3})(?:\s*·\s*(\d{2}\.\d{2}\.\d{4}))?(?:\s*·\s*([\s\S]*))?$/;
  const revoke = /^Оплата\s*#(\d+)(?:\s*\([^)]*\))?\s+отозвана(?:\s*·\s*([\s\S]*))?$/;
  const request = /^Запрос на отзыв оплаты\s*#(\d+)(?:\s*\([^)]*\))?(?:\s*·\s*([\s\S]*))?$/;
  const events = (activities ?? []).filter(a => a.activityType?.code === PAYMENT_ACTIVITY_CODE).sort(byCreated);
  const list: DealPayment[] = [];
  const origin = new Map<number, DealActivity>();
  for (const a of events) {
    const text = (a.actorMessage || '').trim();
    const role = a.actorRole?.code;
    let m: RegExpMatchArray | null;
    if ((m = text.match(edit))) {
      const p = list.find(x => x.activityId === +m![1]);
      const o = p && origin.get(p.activityId);
      const amount = num(m[2]);
      // Менять может только автор, в течение 10 минут; платёжку клиента — пока её не подтвердили
      const confirmedBefore = !!o && o.completed && !(time(o.updatedAt) > time(a.createdAt));
      if (!p || !o || p.revoked || role !== o.actorRole?.code || !inWindow(o.createdAt, a.createdAt)
        || (p.fromOwner && confirmedBefore) || !isFinite(amount) || amount <= 0) continue;
      Object.assign(p, { amount, currency: m[3], date: m[4] || p.date, note: (m[5] || '').trim(), edited: true });
      p.fileActivityIds.push(a.id);
      continue;
    }
    if ((m = text.match(revoke))) {
      const p = list.find(x => x.activityId === +m![1]);
      const o = p && origin.get(p.activityId);
      if (!p || !o || p.revoked) continue;
      const confirmedBefore = o.completed && !(time(o.updatedAt) > time(a.createdAt));
      // Сам отзывает только клиент — свою платёжку, пока её не подтвердили
      const allowed = role === OWNER && p.fromOwner && !confirmedBefore;
      if (allowed) Object.assign(p, { revoked: true, revokeReason: (m[2] || '').trim(), revokeRequest: null });
      continue;
    }
    if ((m = text.match(request))) {
      const p = list.find(x => x.activityId === +m![1]);
      if (!p || p.revoked || role !== OWNER) continue;
      const reason = (m[2] || '').trim();
      if (!a.completed) p.revokeRequest = { activityId: a.id, reason, state: 'pending' };
      else if ((a.targetMessage || '').trim().startsWith(REVOKE_APPROVED)) {
        Object.assign(p, { revoked: true, revokeReason: reason, revokeRequest: null });
      } else p.revokeRequest = { activityId: a.id, reason, state: 'declined' };
      continue;
    }
    m = text.match(re);
    if (!m) continue;
    const amount = num(m[1]);
    if (!isFinite(amount) || amount <= 0) continue;
    // Платёжку грузовладельца экспедитор подтверждает: до этого событие — «запрос» без отметки о выполнении
    const fromOwner = role === OWNER;
    origin.set(a.id, a);
    list.push({
      activityId: a.id, amount, currency: m[2], date: m[3] || '', note: (m[4] || '').trim(), createdAt: a.createdAt,
      fromOwner, pending: fromOwner && !a.completed,
      edited: false, revoked: false, revokeReason: '', revokeRequest: null, fileActivityIds: [a.id],
    });
  }
  return list;
}

/** Итоги по сделке — по каждой валюте; плюс поля в ₸ для сводок (там считаются только счета в тенге). */
export function paymentSummary(activities: DealActivity[] | undefined | null) {
  const invoices = parseInvoices(activities);
  const payments = parsePayments(activities);
  const map = new Map<string, CurrencyTotals>();
  const get = (cur: string) => {
    if (!map.has(cur)) map.set(cur, { currency: cur, invoiced: 0, paid: 0, pending: 0, debt: 0, overpaid: 0 });
    return map.get(cur)!;
  };
  invoices.filter(i => !i.revoked).forEach(i => (get(i.currency).invoiced += i.amount));
  payments.filter(p => !p.revoked).forEach(p => {
    const t = get(p.currency);
    if (p.pending) t.pending += p.amount;
    else t.paid += p.amount;
  });
  const totals = [...map.values()].map(t => ({ ...t, debt: Math.max(0, t.invoiced - t.paid), overpaid: Math.max(0, t.paid - t.invoiced) }));
  // Тенге — первым, остальные по порядку появления
  totals.sort((a, b) => (a.currency === 'KZT' ? -1 : b.currency === 'KZT' ? 1 : 0));
  const kzt = totals.find(t => t.currency === 'KZT');
  return {
    invoices,
    /** Действующие (не отозванные) счета. */
    activeInvoices: invoices.filter(i => !i.revoked),
    payments,
    totals,
    hasDebt: totals.some(t => t.debt > 0),
    hasPending: totals.some(t => t.pending > 0),
    hasOverpay: totals.some(t => t.overpaid > 0),
    invoicedKzt: kzt?.invoiced ?? 0,
    paidKzt: kzt?.paid ?? 0,
    pendingKzt: kzt?.pending ?? 0,
    debtKzt: kzt?.debt ?? 0,
    overpaidKzt: kzt?.overpaid ?? 0,
  };
}
